import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Loader2, UserPlus, Shield, Users, AlertCircle, RefreshCw, Ban, Settings2} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface TeamMember {
  id: string;
  userId: string;
  orgId: string;
  role: string;
  title: string | null;
  status: string;
  permissions: Record<string, boolean> | null;
  invitedAt: string | null;
  inviteResentAt: string | null;
  acceptedAt: string | null;
  user: { id: string; email: string; firstName: string | null; lastName: string | null };
  inviter: { id: string; email: string; firstName: string | null; lastName: string | null } | null;
}

interface RolesData {
  roles: string[];
  permissions: string[];
  templates: Record<string, Record<string, boolean>>;
}

function formatRoleName(role: string): string {
  return role.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

function getStatusBadge(status: string) {
  switch (status) {
    case "active":
      return <Badge className="bg-green-600 hover:bg-green-600 text-xs py-0">Active</Badge>;
    case "invited":
      return <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-xs py-0">Invited</Badge>;
    case "pending":
      return <Badge variant="secondary" className="text-xs py-0">Pending</Badge>;
    case "revoked":
      return <Badge variant="destructive" className="text-xs py-0 opacity-80">Revoked</Badge>;
    case "suspended":
      return <Badge variant="destructive" className="text-xs py-0">Suspended</Badge>;
    default:
      return <Badge variant="outline" className="text-xs py-0">{status}</Badge>;
  }
}

const TITLE_SUGGESTIONS = ["Sales Rep", "Customer Service Rep", "Operations", "Warehouse", "Executive"];

export default function TeamPage() {
  const { sessionId, user } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ email: "", firstName: "", lastName: "", role: "standard_user", title: "Sales Rep" });
  const [editing, setEditing] = useState<TeamMember | null>(null);

  const { data: members = [], isLoading } = useQuery<TeamMember[]>({
    queryKey: ["/api/team/members"],
    queryFn: () => api.get<TeamMember[]>(`/team/members`, sessionId),
  });

  const { data: rolesData } = useQuery<RolesData>({
    queryKey: ["/api/team/roles"],
    queryFn: () => api.get<RolesData>(`/team/roles`, sessionId),
  });

  const inviteMutation = useMutation({
    mutationFn: () => api.post(`/team/invite`, invite, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team/members"] });
      setShowInvite(false);
      setInvite({ email: "", firstName: "", lastName: "", role: "standard_user", title: "Sales Rep" });
      toast({ title: "Invitation sent", description: `Invite sent to ${invite.email}` });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: any }) =>
      api.patch(`/team/members/${id}`, patch, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team/members"] });
      toast({ title: "Member updated" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => api.post(`/team/members/${id}/resend-invite`, undefined, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team/members"] });
      toast({ title: "Invite resent" });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.post(`/team/members/${id}/revoke`, undefined, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team/members"] });
      toast({ title: "Member revoked" });
    },
  });

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Team"
        description="Manage users and permissions in your organization"
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setShowInvite(true)} data-testid="button-invite-user">
              <UserPlus className="h-4 w-4 mr-1" /> Invite User
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4" /> Team Members ({members.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No team members yet.</p>
          ) : (
            <div className="space-y-1">
              {members.map((m) => (
                <div key={m.id} className="flex items-center justify-between py-2 px-2 rounded-md hover:bg-muted/40">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="text-xs">
                        {m.user.firstName?.[0]}{m.user.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">
                          {m.user.firstName} {m.user.lastName}
                        </span>
                        {m.userId === user?.id && (
                          <Badge variant="outline" className="text-[10px] py-0">You</Badge>
                        )}
                        {getStatusBadge(m.status)}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{m.user.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      value={m.role}
                      disabled={m.userId === user?.id || m.status === "revoked"}
                      onValueChange={(role) => updateMutation.mutate({ id: m.id, patch: { role } })}
                    >
                      <SelectTrigger className="h-8 text-xs w-[150px]">
                        <SelectValue>{formatRoleName(m.role)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {(rolesData?.roles || []).map((r) => (
                          <SelectItem key={r} value={r}>{formatRoleName(r)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      className="h-8 text-xs w-[160px]"
                      defaultValue={m.title || ""}
                      placeholder="Title"
                      disabled={m.status === "revoked"}
                      onBlur={(e) => {
                        const next = e.currentTarget.value.trim();
                        if ((m.title || "") !== next) {
                          updateMutation.mutate({ id: m.id, patch: { title: next || null } });
                        }
                      }}
                    />
                    <Button
                      size="icon" variant="ghost" className="h-7 w-7"
                      onClick={() => setEditing(m)}
                    >
                      <Settings2 className="h-3.5 w-3.5" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-7 w-7">
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {(m.status === "invited" || m.status === "pending") && (
                          <DropdownMenuItem onClick={() => resendMutation.mutate(m.id)}>
                            <RefreshCw className="h-4 w-4 mr-2" /> Resend invite
                          </DropdownMenuItem>
                        )}
                        {m.status !== "revoked" && m.userId !== user?.id && (
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => {
                              if (confirm(`Revoke ${m.user.firstName} ${m.user.lastName}'s access?`)) {
                                revokeMutation.mutate(m.id);
                              }
                            }}
                          >
                            <Ban className="h-4 w-4 mr-2" /> Revoke access
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation email to add a new member to your organization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Email *</label>
              <Input
                type="email"
                placeholder="colleague@company.com"
                value={invite.email}
                onChange={(e) => setInvite({ ...invite, email: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">First name</label>
                <Input
                  value={invite.firstName}
                  onChange={(e) => setInvite({ ...invite, firstName: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Last name</label>
                <Input
                  value={invite.lastName}
                  onChange={(e) => setInvite({ ...invite, lastName: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Access Level</label>
              <Select value={invite.role} onValueChange={(role) => setInvite({ ...invite, role })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(rolesData?.roles || []).map((r) => (
                    <SelectItem key={r} value={r}>{formatRoleName(r)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Controls org-wide permissions. Title (below) is just a label.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Title *</label>
              <Input
                list="invite-title-suggestions"
                value={invite.title}
                onChange={(e) => setInvite({ ...invite, title: e.target.value })}
                placeholder="e.g. Sales Rep, VP of Sales, Operations"
              />
              <datalist id="invite-title-suggestions">
                {TITLE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
              </datalist>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button
              onClick={() => inviteMutation.mutate()}
              disabled={!invite.email.trim() || !invite.title.trim() || inviteMutation.isPending}
            >
              {inviteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permission editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" /> Edit Permissions
            </DialogTitle>
            <DialogDescription>
              {editing && `Customize permission overrides for ${editing.user.firstName} ${editing.user.lastName}.`}
            </DialogDescription>
          </DialogHeader>
          {editing && rolesData && (
            <PermissionEditor
              member={editing}
              rolesData={rolesData}
              onSave={(perms) => {
                updateMutation.mutate({ id: editing.id, patch: { permissions: perms } });
                setEditing(null);
              }}
              onClose={() => setEditing(null)}
              currentUserId={user?.id}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PermissionEditor({
  member, rolesData, onSave, onClose, currentUserId,
}: {
  member: TeamMember;
  rolesData: RolesData;
  onSave: (perms: Record<string, boolean>) => void;
  onClose: () => void;
  currentUserId?: string;
}) {
  const [overrides, setOverrides] = useState<Record<string, boolean>>(member.permissions || {});
  const isCurrent = member.userId === currentUserId;
  const template = rolesData.templates[member.role] || {};

  const effective = (perm: string) => overrides[perm] !== undefined ? overrides[perm] : !!template[perm];

  const toggle = (perm: string) => {
    const roleDefault = !!template[perm];
    const current = effective(perm);
    if (current === roleDefault && overrides[perm] === undefined) {
      setOverrides({ ...overrides, [perm]: !current });
    } else if (overrides[perm] !== undefined) {
      const copy = { ...overrides };
      delete copy[perm];
      setOverrides(copy);
    } else {
      setOverrides({ ...overrides, [perm]: !current });
    }
  };

  return (
    <div className="space-y-3">
      {isCurrent && (
        <div className="flex items-center gap-2 p-2 rounded-md bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <span className="text-sm text-yellow-800 dark:text-yellow-200">
            You cannot edit your own permissions
          </span>
        </div>
      )}
      <div className="space-y-1.5 max-h-[400px] overflow-auto pr-2">
        {rolesData.permissions.map((perm) => {
          const isOn = effective(perm);
          const overridden = overrides[perm] !== undefined;
          return (
            <div key={perm} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/40">
              <div className="flex items-center gap-2">
                <span className="text-sm">{perm.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</span>
                {overridden && (
                  <Badge variant="outline" className="text-[10px]">Override</Badge>
                )}
              </div>
              <input
                type="checkbox"
                checked={isOn}
                onChange={() => toggle(perm)}
                disabled={isCurrent}
              />
            </div>
          );
        })}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button disabled={isCurrent} onClick={() => onSave(overrides)}>Save Changes</Button>
      </DialogFooter>
    </div>
  );
}
